'use no memo';

import type { Notification } from '@treneo/features/notifications';
import { urls } from '@treneo/shared/constants';
import { useAppTranslations } from '@treneo/shared/i18n';
import { toast } from '@treneo/shared/ui';

import {
  TRAINER_INVITATION_STATUS,
  useAcceptTrainerInvitationMutation,
  useMyPendingTrainerInvitationsQuery,
  useRejectTrainerInvitationMutation
} from '../data';

const NOTIFICATION_TYPE_TRAINER_INVITATION = 'trainer-invitation';

const formatCreatedAt = (value: string | Date | undefined): string | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  return typeof value === 'string' ? value : new Date(value).toISOString();
};

export const useTrainerInvitationNotifications = (enabled: boolean) => {
  const { t } = useAppTranslations();
  const { data, isLoading } = useMyPendingTrainerInvitationsQuery({ enabled });
  const acceptMutation = useAcceptTrainerInvitationMutation();
  const rejectMutation = useRejectTrainerInvitationMutation();

  const rawList = data?.invitations ?? [];
  const list = rawList.filter(inv => {
    const status = (inv as { status?: number }).status;
    return Number(status) === TRAINER_INVITATION_STATUS.PENDING;
  });
  const notifications: Notification[] = list.map(inv => {
    const organizationId = inv.organizationId;
    const token = inv.token;

    const onAccept = async () => {
      await acceptMutation.mutateAsync({ organizationId, token });
      toast.success(t('trainers.invitationAccepted'));
    };

    const onReject = async () => {
      await rejectMutation.mutateAsync({ organizationId, token });
      toast.success(t('trainers.invitationRejected'));
    };

    const invitedBy = t('trainers.invitedBy');
    const description = inv.courseTitle
      ? `${invitedBy} ${inv.inviterName} · ${inv.courseTitle}`
      : `${invitedBy} ${inv.inviterName}`;

    return {
      id: `${NOTIFICATION_TYPE_TRAINER_INVITATION}-${organizationId}-${token}`,
      type: NOTIFICATION_TYPE_TRAINER_INVITATION,
      title: inv.organizationName,
      description,
      detailsUrl: urls.trainerInviteAcceptPageByToken(token),
      acceptLabel: t('trainers.accept'),
      rejectLabel: t('trainers.reject'),
      onAccept,
      onReject,
      createdAt: formatCreatedAt(inv.createdAt)
    };
  });

  return { notifications, isLoading };
};
